import { useState, useEffect } from "react";
import { getMsalInstance, cleanupMsalInstance } from "@/config/auth-config";
import { authLogger } from "@/utilities/auth-logger";
import {
  isAccountTypeError,
  handleAccountTypeError
} from "@/utilities/msal-utils";
import { MsalService } from "@/components/security/msal-service";
import type { MsalInitState } from "@/types/core";

export function useMsalInit() {
  const [state, setState] = useState<MsalInitState>({
    isInitialized: false,
    error: null,
    isLoading: true
  });

  useEffect(() => {
    let isMounted = true;

    const initializeMsal = async () => {
      try {
        setState((prev) => ({ ...prev, isLoading: true, error: null }));

        const msalInstance = getMsalInstance();
        const msalService = MsalService.getInstance(msalInstance);

        authLogger.msalInit("Starting MSAL initialization");

        // Wait for MSAL to be ready
        await msalInstance.initialize();

        authLogger.msalInit("MSAL instance initialized successfully");

        // Handle redirect promise to set active account (MSAL 5.x: options object)
        try {
          const response = await msalInstance.handleRedirectPromise({
            navigateToLoginRequestUrl: true
          });
          if (response) {
            authLogger.redirect("Redirect response received", {
              account: response.account?.username,
              authority: response.authority
            });

            // Set the active account after successful redirect
            msalService.setActiveAccount(response.account);
          }
        } catch (redirectError) {
          // Handle AADSTS500207 account type errors specifically
          if (isAccountTypeError(redirectError)) {
            authLogger.warn(
              "AADSTS500207 account type error detected, handling..."
            );
            await handleAccountTypeError(msalInstance, redirectError);
            return; // Exit initialization as we're redirecting
          }

          authLogger.authError(redirectError, "handleRedirectPromise");
          // Continue initialization despite redirect error
        }

        const activeAccount = msalService.getActiveAccount();
        if (activeAccount) {
          authLogger.debug("Active account found", {
            userId: activeAccount.username
          });
        } else {
          // No accounts found - MSAL will handle this through its internal logic
          authLogger.debug("No accounts found in MSAL cache");
        }

        // Additional fallback: Check if we're on a redirect page but no response was handled
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(
          window.location.hash.substring(1)
        );
        const hasAuthParams =
          urlParams.has("code") ||
          urlParams.has("error") ||
          hashParams.has("access_token") ||
          hashParams.has("error");

        if (hasAuthParams && !activeAccount) {
          authLogger.warn(
            "Auth parameters detected but no account found - clearing cache and redirecting"
          );

          try {
            await msalInstance.clearCache();
            window.location.href = "/login";
            return;
          } catch (clearError) {
            authLogger.authError(clearError, "clearCache");
          }
        }

        // MSAL initialization complete
        const finalActiveAccount = msalService.getActiveAccount();
        if (finalActiveAccount) {
          authLogger.debug("MSAL initialization complete with active account", {
            username: finalActiveAccount.username
          });
        }

        if (isMounted) {
          setState({
            isInitialized: true,
            error: null,
            isLoading: false
          });

          authLogger.msalInit("MSAL initialization completed successfully");
        }
      } catch (error) {
        if (isMounted) {
          const errorMessage =
            error instanceof Error ? error.message : "Unknown error";
          setState({
            isInitialized: false,
            error: errorMessage,
            isLoading: false
          });

          authLogger.authError(error, "MSAL initialization");
        }
      }
    };

    initializeMsal();

    // Cleanup function
    return () => {
      isMounted = false;
      if (import.meta.env.DEV) {
        cleanupMsalInstance();
      }
      // Note: Session management cleanup removed
    };
  }, []);

  return state;
}
